import * as Tone from 'tone';
import { isSessionLocal } from '../state.js';
import { SAMPLE_LIFESPAN_MS } from '../soul/evolve.js';

// Caches decoded buffers. Plays random fragments of a recorded voice,
// pitched to the current key, through the shared effect chain.

export class SampleBank {
  constructor() {
    this.buffers = new Map(); // id -> Tone.ToneAudioBuffer
    this.loadAttempts = new Map(); // id -> failed load count, drives retry backoff
    this.samples = [];        // full list from soul
    // diagnostics — how many trigger calls land vs. bail, and why
    this.stats = { attempted: 0, played: 0, noSamples: 0, noneLoaded: 0, noBuffer: 0 };
    this._lastStatsLog = performance.now();
    // optional callback for load lifecycle observability — main.js wires this
    // up to drive the on-screen yellow/green/red glow.
    this.loadListener = null;
  }

  setLoadListener(cb) { this.loadListener = cb; }
  _emitLoad(payload) { try { this.loadListener?.(payload); } catch (e) { console.error(e); } }

  logStats(now = performance.now()) {
    if (now - this._lastStatsLog < 15000) return;
    this._lastStatsLog = now;
    const loadedCount = [...this.buffers.values()].filter(b => b.loaded).length;
    // eslint-disable-next-line no-console
    console.log('[samples]', {
      pool: this.samples.length,
      loaded: loadedCount,
      attempts15s: this.stats.attempted,
      played15s: this.stats.played,
      skip_noSamples: this.stats.noSamples,
      skip_noneLoaded: this.stats.noneLoaded,
      skip_noBuffer: this.stats.noBuffer,
    });
    this.stats = { attempted: 0, played: 0, noSamples: 0, noneLoaded: 0, noBuffer: 0 };
  }

  update(samples) {
    this.samples = samples || [];
    // prefetch all samples — previously we only loaded the first 8, which meant
    // once the pool grew past 8, older voices could never be picked because
    // their buffers never became `loaded`.
    for (const s of this.samples) this._ensureLoaded(s);
    // prune buffers for samples no longer present
    const present = new Set(this.samples.map(s => s.id));
    for (const id of [...this.buffers.keys()]) {
      if (!present.has(id)) {
        const b = this.buffers.get(id);
        try { b.dispose(); } catch {}
        this.buffers.delete(id);
        this.loadAttempts.delete(id);
      }
    }
  }

  _ensureLoaded(s) {
    if (this.buffers.has(s.id)) return;
    this._emitLoad({ phase: 'start', id: s.id, url: s.url });
    const buf = new Tone.ToneAudioBuffer(
      s.url,
      () => {
        this.loadAttempts.delete(s.id);
        this._emitLoad({ phase: 'success', id: s.id });
      },
      (err) => {
        const attempt = (this.loadAttempts.get(s.id) || 0) + 1;
        this.loadAttempts.set(s.id, attempt);
        console.warn('[samples] load failed', s.id, s.url, err, `attempt ${attempt}`);
        this._emitLoad({ phase: 'fail', id: s.id, error: err });
        this.buffers.delete(s.id);
        // A sample another listener just uploaded can 404 for a few seconds
        // until Supabase's CDN has the object. Without a retry it sat in the
        // pool unplayable until the next pool change (minutes). Back off
        // 3s -> 8s -> 20s, then give up until the pool changes.
        const RETRY_MS = [3000, 8000, 20000];
        if (attempt <= RETRY_MS.length) {
          setTimeout(() => {
            const stillInPool = this.samples.some((x) => x.id === s.id);
            if (stillInPool && !this.buffers.has(s.id)) this._ensureLoaded(s);
          }, RETRY_MS[attempt - 1]);
        }
      }
    );
    this.buffers.set(s.id, buf);
  }

  // weighted random pick from loaded samples.
  //   session-local (uploaded this tab):     4×   — listener hears their voice often
  //   fresh (default-tier non-elder):        1×   — baseline
  //   elder (week-or-longer-tier):           0.35× — present but quiet ghosts
  // these weights were chosen so a single fresh-sample pool plays uniformly,
  // a single session-local sample is heard ~4× more than a same-pool fresh,
  // and a 6-elder fill on a quiet day still leaves elders as a minority of
  // the actual playback even though they may take up many slots.
  pickWeighted() {
    if (!this.samples.length) return null;
    const loaded = this.samples.filter((s) => {
      const b = this.buffers.get(s.id);
      return b && b.loaded;
    });
    if (!loaded.length) return null;
    const weights = new Array(loaded.length);
    let total = 0;
    for (let i = 0; i < loaded.length; i++) {
      const s = loaded[i];
      const w = isSessionLocal(s.id) ? 4
              : Number(s.lifespan_ms) > SAMPLE_LIFESPAN_MS ? 0.35
              : 1;
      weights[i] = w;
      total += w;
    }
    let r = Math.random() * total;
    for (let i = 0; i < loaded.length; i++) {
      r -= weights[i];
      if (r <= 0) return loaded[i];
    }
    return loaded[loaded.length - 1];
  }

  /**
   * Trigger a chopped, pitched fragment.
   * @param {Tone.ToneAudioNode} dest  connect player here (effect chain input)
   * @param {number} semitones         pitch offset
   * @param {number} velocity          0..1
   */
  trigger(dest, semitones = 0, velocity = 0.7, when) {
    this.stats.attempted++;
    this.logStats();
    if (!this.samples.length) { this.stats.noSamples++; return; }
    const sample = this.pickWeighted();
    if (!sample) { this.stats.noneLoaded++; return; }
    const buffer = this.buffers.get(sample.id);
    if (!buffer || !buffer.loaded) { this.stats.noBuffer++; return; }
    const duration = buffer.duration;
    if (duration <= 0.05) return;
    this.stats.played++;

    // Three-bucket length distribution:
    //   15%  long    — 1.5s–4s capped to recording length
    //   50%  medium  — 400ms–1.8s, the 'slightly longer' feel
    //   35%  micro   — 60ms–300ms chopped stutter-like grains
    const r = Math.random();
    let fragLen;
    if (r < 0.15 && duration >= 1.6) {
      const longMax = Math.min(4.0, duration * 0.9);
      fragLen = 1.5 + Math.random() * Math.max(0, longMax - 1.5);
      velocity *= 0.85;   // soften long fragments a touch (was 0.7 — too quiet)
    } else if (r < 0.65) {
      const minLen = 0.4;
      const maxLen = Math.min(1.8, duration * 0.9);
      // softer bias (was r^1.3) — pulls the median up from ~0.97s to ~1.1s
      fragLen = minLen + Math.pow(Math.random(), 1.1) * (maxLen - minLen);
    } else {
      const minLen = 0.06;
      const maxLen = Math.min(0.3, duration * 0.9);
      // softer bias (was r^1.5) — pulls the median up from ~0.14s to ~0.18s
      fragLen = minLen + Math.pow(Math.random(), 1.2) * (maxLen - minLen);
    }
    // floor the effective velocity so no voice fragment ever lands
    // inaudible — minimum ~-6 dB pre-makeup so voices always sit up.
    const effectiveVelocity = Math.max(0.5, velocity);
    const start = Math.random() * Math.max(0.0001, duration - fragLen - 0.01);

    // scale fades to fragment length so micro stutters don't get swallowed
    const fadeIn  = Math.min(0.01, fragLen * 0.2);
    const fadeOut = Math.min(0.04, fragLen * 0.35);
    const player = new Tone.Player({
      url: buffer,
      playbackRate: Math.pow(2, semitones / 12),
      fadeIn,
      fadeOut,
    });
    // mono MediaRecorder output can collapse to one channel through some stereo
    // effects (pingpong / phaser / pitchshift). route each sample through a
    // Panner. most of the time the pan stays near center (±0.15); ~15% of
    // the time a sample is intentionally pushed out to one side (0.3 to 0.75
    // left or right) for stereo variety.
    const wide = Math.random() < 0.15;
    const panPos = wide
      ? (Math.random() < 0.5 ? -1 : 1) * (0.3 + Math.random() * 0.45)
      : -0.15 + Math.random() * 0.3;
    const pan = new Tone.Panner(panPos);
    // +5 dB makeup so voices sit on top of the bed instead of behind it
    const vol = new Tone.Volume(Tone.gainToDb(effectiveVelocity) + 5).connect(dest);
    player.connect(pan);
    pan.connect(vol);
    const t = when ?? Tone.now();
    try {
      player.start(t, start, fragLen);
    } catch (e) { /* race — buffer swapped out */ }
    player.onstop = () => { try { player.dispose(); pan.dispose(); vol.dispose(); } catch {} };
  }
}
